import React, { ReactNode, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Colors } from '@pukka/common/types';
import { twMerge } from 'tailwind-merge';

import { getStrokeWidth } from './util';

interface LoaderCommonProps {
  readonly value?: number;
  readonly useLabel?: true | ReactNode;
  readonly color?: `text-${Colors}`;
  readonly trackColor?: `text-${Colors}`;
  readonly className?: string;
}

interface LoaderBarProps extends LoaderCommonProps {
  readonly type?: 'bar';
}

interface LoaderRadialProps extends LoaderCommonProps {
  readonly type: 'radial';
  readonly size?: number;
}

type LoaderProps = LoaderBarProps | LoaderRadialProps;

interface LoaderRef {
  progress: number;
  isProgressing: boolean;
}

const DASHARRAY = 2 * Math.PI * 30;

const LoaderWithProgress = forwardRef<LoaderRef, LoaderProps>(
  ({ type = 'bar', value, className, useLabel, color, trackColor = 'text-black/20', ...props }, ref) => {
    const [isProgressing, setIsProgressing] = useState(!!value);
    const text = useRef<HTMLParagraphElement>(null);
    const circle = useRef<SVGCircleElement>(null);
    const bar = useRef<HTMLDivElement>(null);
    const progress = useRef(0);
    const size = (props as LoaderRadialProps).size ?? 24;

    const onUpdateProgress = useCallback(
      (val: number) => {
        progress.current = Math.max(Math.min(val, 100), 0);

        if (text.current) {
          // Update the text without rerendering
          text.current.innerHTML = `${progress.current.toFixed(1)}%`;
        }

        if (circle.current) {
          const dashOffset = DASHARRAY * ((100 - progress.current) / 100);

          circle.current.setAttribute('stroke-dashoffset', String(dashOffset));
        }

        if (bar.current) {
          bar.current.style.width = `${progress.current}%`;
        }

        if (progress.current > 0 && !isProgressing) {
          setIsProgressing(true);
        }

        if (progress.current === 0 && isProgressing) {
          setIsProgressing(false);
        }
      },
      [isProgressing],
    );

    // Use the ref.progress to enhance the rendering
    // We can use props just fine, but it will trigger re-render each time the value updated
    // And it might be costly
    useImperativeHandle(
      ref,
      () => {
        return {
          get progress() {
            return progress.current;
          },
          set progress(val: number) {
            onUpdateProgress(val);
          },
          get isProgressing() {
            return isProgressing;
          },
          set isProgressing(progressing: boolean) {
            setIsProgressing(progressing);
          },
        };
      },
      [isProgressing, onUpdateProgress],
    );

    useEffect(() => {
      onUpdateProgress(value ?? 0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return type === 'radial' ? (
      <div role="status" className={twMerge('flex items-center', color, className)}>
        <svg
          role="status"
          width={size}
          height={size}
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid"
          className="-rotate-90 stroke-current"
        >
          <circle
            strokeLinecap="round"
            strokeWidth={getStrokeWidth(size)}
            fill="none"
            r="30"
            cx="50"
            cy="50"
            className={twMerge('stroke-current', trackColor)}
          />
          {isProgressing ? (
            <circle
              data-testid="progressing"
              ref={circle}
              cx="50"
              cy="50"
              r="30"
              fill="none"
              strokeWidth={getStrokeWidth(size)}
              strokeDasharray={DASHARRAY}
              strokeLinecap="round"
              className="duration-300 ease-out"
            />
          ) : (
            <circle data-testid="indefinite" cx="50" cy="50" r="30" strokeWidth={getStrokeWidth(size)} strokeLinecap="round" fill="none">
              <animateTransform
                attributeName="transform"
                type="rotate"
                repeatCount="indefinite"
                dur={`${1}s`}
                values="0 50 50;180 50 50;720 50 50"
                keyTimes="0;0.5;1"
              />
              <animate
                attributeName="stroke-dasharray"
                repeatCount="indefinite"
                dur={`${1}s`}
                values="18.84955592153876 169.64600329384882;94.2477796076938 94.24777960769377;18.84955592153876 169.64600329384882"
                keyTimes="0;0.5;1"
              />
            </circle>
          )}
        </svg>
        {useLabel === true ? <p className="ml-1 mr-2 text-xs" ref={text} /> : useLabel ?? null}
      </div>
    ) : (
      <div role="status" className={twMerge('relative h-full min-h-[0.25rem] w-full', className)}>
        <div className={twMerge('absolute left-0 top-0 h-full w-full bg-current', trackColor)} />
        {isProgressing ? (
          <div
            key="progressing"
            data-testid="progressing"
            ref={bar}
            className={twMerge('absolute left-0 top-0 h-full bg-current duration-300 ease-out', color)}
          />
        ) : (
          <div
            key="indefinite"
            data-testid="indefinite"
            className={twMerge('absolute left-0 top-0 h-full animate-loadingbar bg-current', color)}
          />
        )}
        {useLabel === true ? (
          <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs mix-blend-difference" ref={text} />
        ) : (
          useLabel ?? null
        )}
      </div>
    );
  },
);

export { LoaderWithProgress, type LoaderRef };
